import { useQuery } from '@tanstack/react-query';
import { fetchUserEmails } from './service';
import { UserEmailsQueryKeys } from '../../queryKeys';
import { AxiosError, AxiosInstance } from 'axios';
import { UseUserEmailsProps, UseUserEmailsQueryKeyProps } from './props';
import { useEffect, useMemo, useState } from 'react';
import { useAxiosInstance, usePaginatedQuery } from '@starsoft/common/hooks';
import {
  GenericError,
  PaginatedRequest,
  PaginatedResponse,
  UserEmail,
} from '@starsoft/common/models';
import { AuthService } from '@starsoft/common/services';
import { setCookie } from 'cookies-next';
import { CookiesKey } from '@starsoft/common/enums';

/**
 * Custom hook to fetch and manage user emails with pagination
 *
 * This hook handles fetching user emails, managing pagination state, tracking primary email,
 * and handling cookie updates for email tracking. It uses React Query for data fetching and caching.
 *
 * @param {UseUserEmailsProps} options - Optional configuration object
 * @param {Function} options.externalOnSuccess - Optional callback function called when data is successfully fetched
 * @param {boolean} options.enabled - Optional flag to enable/disable the query (defaults to true)
 *
 * @returns {Object} An object containing:
 *   - page: Current page number
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually trigger a refetch
 *   - isError: Boolean indicating if an error occurred
 *   - emails: Paginated response containing user emails
 *   - primaryEmail: User's primary email if exists
 *   - fetchNextPage: Function to fetch next page
 *   - setCurrentPage: Function to set specific page
 *   - fetchPreviousPage: Function to fetch previous page
 *   - isLoading: Boolean indicating if fetch is in progress (excluding idle state)
 */
export function useUserEmails(options?: UseUserEmailsProps) {
  const { externalOnSuccess, enabled = true } = options ?? {};
  const [page, setPage] = useState<number>(1);
  const params: PaginatedRequest = {
    page,
    limit: 50,
  };
  const axiosInstance: AxiosInstance = useAxiosInstance();

  const queryKey: UseUserEmailsQueryKeyProps = [UserEmailsQueryKeys.Me, params];

  const { data, isPending, isError, error, refetch, fetchStatus } = useQuery<
    PaginatedResponse<UserEmail>,
    AxiosError<GenericError>,
    PaginatedResponse<UserEmail>,
    UseUserEmailsQueryKeyProps
  >({
    queryKey,
    queryFn: queryContext => fetchUserEmails(queryContext, axiosInstance),
    staleTime: Infinity,
    enabled: AuthService.isPartiallyAuthorized() && enabled,
  });

  // Find and memoize the primary email from the fetched data
  const primaryEmail: UserEmail | undefined = useMemo(
    () => data?.data?.find(email => email?.primary),
    [data],
  );

  // Setup pagination controls using custom hook
  const { fetchNextPage, fetchPreviousPage, setCurrentPage } =
    usePaginatedQuery({
      page,
      setPage,
      data,
    });

  /**
   * Handles calling the external success callback if provided and data exists
   */
  function handleExternalOnSuccess() {
    if (!data || typeof externalOnSuccess == 'undefined') {
      return;
    }

    externalOnSuccess?.(data);
  }

  //eslint-disable-next-line
  useEffect(handleExternalOnSuccess, [data]);

  /**
   * Updates the user email tracking cookie when primary email changes
   * Sets cookie with 90 day expiration
   */
  function handleUpdateUserEmailTrackCookie() {
    if (!primaryEmail) {
      return;
    }

    setCookie(CookiesKey.UserEmail, primaryEmail?.email, {
      //@ts-ignore
      maxAge: 90 * 24 * 60 * 60 * 1000, //90 days,
    });
  }

  useEffect(handleUpdateUserEmailTrackCookie, [primaryEmail]);

  return {
    page,
    error,
    refetch,
    isError,
    emails: data,
    primaryEmail,
    fetchNextPage,
    setCurrentPage,
    fetchPreviousPage,
    isLoading: isPending && fetchStatus != 'idle',
  };
}
